import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../redux/messageSlice';
import { updateState } from '../../redux/commonSlice';
import { getQuickTurnsApi, acceptQuickTurnApi, rejectQuickTurnApi } from '../../services/api';

// Default error messages, used when the API response has no `message` field.
const DEFAULT_ERROR = {
  load: 'Failed to load quick turn requests',
  accept: 'Failed to accept request',
  reject: 'Failed to reject request',
};

const usePendingQuickTurnActions = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [actionLoading, setActionLoading] = useState(null); // id of item being actioned
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // GET /api/v1/quick_turns — paginated pending review queue.
  // append=true fetches the next page and adds it to the existing list (used by
  // the "load more" control); otherwise the list is replaced (initial load / refresh).
  const getQuickTurnRequests = async ({ append = false, pageNumber = 1 } = {}, callback) => {
    setIsLoading(true);
    try {
      const response = await getQuickTurnsApi({ page: pageNumber });
      if (response?.data?.http_code === 200) {
        const { quick_turns = [], total_count = 0, next_page } = response.data.data || {};
        setRequests((prev) => (append ? [...prev, ...quick_turns] : quick_turns));
        setTotalCount(total_count);
        setHasNextPage(Boolean(next_page));
        setPage(pageNumber);
        // Keep the header badge in sync with the pending queue size.
        dispatch(updateState({ pending_quick_turn_count: total_count }));
      }
      callback && callback();
    } catch (error) {
      const msg = error?.response?.data?.message || DEFAULT_ERROR.load;
      dispatch(setSnackbar({ flag: true, type: 'error', message: msg }));
    } finally {
      setIsLoading(false);
    }
  };

  const loadNextPage = () => {
    if (!hasNextPage || isLoading) return;
    getQuickTurnRequests({ append: true, pageNumber: page + 1 });
  };

  // Called by dashboard / board-summary hook to seed the badge count without
  // fetching the full list.
  const syncQuickTurnCount = (count) => {
    dispatch(updateState({ pending_quick_turn_count: count }));
  };

  const removeRequestFromList = (id) => {
    setRequests((prev) => prev.filter((r) => r.id !== id));
    setTotalCount((prev) => Math.max(prev - 1, 0));
  };

  // POST /api/v1/quick_turns/accept
  const acceptQuickTurnRequest = async (id, callback) => {
    setActionLoading(id);
    try {
      const response = await acceptQuickTurnApi({ id });
      if (response?.data?.http_code === 200) {
        dispatch(setSnackbar({ flag: true, type: 'success', message: response.data.message }));
        removeRequestFromList(id);
        dispatch(updateState({ pending_quick_turn_count: Math.max(totalCount - 1, 0) }));
        callback && callback();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || DEFAULT_ERROR.accept;
      dispatch(setSnackbar({ flag: true, type: 'error', message: msg }));
    } finally {
      setActionLoading(null);
    }
  };

  // POST /api/v1/quick_turns/reject — rejection_note is required by the API (400 if missing).
  const rejectQuickTurnRequest = async (id, rejectionNote, callback) => {
    setActionLoading(id);
    try {
      const response = await rejectQuickTurnApi({ id, rejection_note: rejectionNote });
      if (response?.data?.http_code === 200) {
        dispatch(setSnackbar({ flag: true, type: 'success', message: response.data.message }));
        removeRequestFromList(id);
        dispatch(updateState({ pending_quick_turn_count: Math.max(totalCount - 1, 0) }));
        callback && callback();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || DEFAULT_ERROR.reject;
      dispatch(setSnackbar({ flag: true, type: 'error', message: msg }));
    } finally {
      setActionLoading(null);
    }
  };

  return {
    getQuickTurnRequests,
    loadNextPage,
    syncQuickTurnCount,
    acceptQuickTurnRequest,
    rejectQuickTurnRequest,
    requests,
    isLoading,
    actionLoading,
    totalCount,
    hasNextPage,
  };
};

export default usePendingQuickTurnActions;
